import { useState, useMemo, useCallback, useEffect } from 'react';
import { Button, Form, FormInstance, Input, Tag } from 'antd';
import { EditOutlined, CloseOutlined } from '@ant-design/icons';
import { CrudSchemaFields, type EnsoFormItem } from '@ohpen/enso-ui';
import { useTranslation } from 'react-i18next';
import { AddressDetailsResponse, FirmFcaData } from '@pmp/api';
import { DEFAULT_TEXT_MAX_LENGTH_SHORT, textLimitProps } from '@pmp/utils';
import { AddressLookup } from '../../AddressLookup';
import { formatAddressAsLines, getHighlightProps } from '../utils';
import styles from '../styles.module.css';

interface AddressFieldProps {
  readonly: boolean;
  form?: FormInstance;
  getAddress?: () => any;
  fcaModifiedFields?: FirmFcaData | null;
  removeFcaModifiedField?: (key: keyof FirmFcaData, nestedKey?: string) => void;
  isFromFca?: boolean;
  namePrefix?: (string | number)[];
  titleKey?: string;
  fcaFieldKey?: keyof FirmFcaData;
  fcaFieldNestedKey?: string;
  initialEditing?: boolean;
  /** Non-zero value expands the section; a new value re-expands it after the user collapsed it. */
  forceEditing?: number;
  /**
   * Marks line 1 / town / post code with the required asterisk. Only trading addresses are
   * mandatory server-side — the registered office address has no such rule.
   */
  markRequired?: boolean;
  enableLookup?: boolean;
  /**
   * Absolute form path of the address object. Needed alongside namePrefix because inside a
   * Form.List the prefix is list-relative, while setFieldValue requires the full path.
   */
  lookupBasePath?: (string | number)[];
  onAddressApplied?: () => void;
}

export const AddressField: React.FC<AddressFieldProps> = ({
  readonly,
  form,
  getAddress,
  fcaModifiedFields,
  removeFcaModifiedField,
  isFromFca,
  namePrefix,
  titleKey = 'firms.labels.tradingOfficeAddress',
  fcaFieldKey = 'tradingNames',
  fcaFieldNestedKey,
  initialEditing = false,
  forceEditing = 0,
  markRequired = false,
  enableLookup = false,
  lookupBasePath,
  onAddressApplied,
}) => {
  const prefixPath = useCallback(
    (path: (string | number)[]) => (namePrefix ? [...namePrefix, ...path] : path),
    [namePrefix],
  );
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(initialEditing);

  useEffect(() => {
    if (forceEditing) setIsEditing(true);
  }, [forceEditing]);

  const type = Form.useWatch(['registrationDetails', 'type'], form);

  const isFcaRegistered = useMemo(() => type !== 'NOT_FCA_REGISTERED', [type]);

  const activeFcaFields = useMemo(() => (isFromFca ? fcaModifiedFields : null), [isFromFca, fcaModifiedFields]);
  const clearFca = useCallback(
    () => removeFcaModifiedField?.(fcaFieldKey, fcaFieldNestedKey),
    [removeFcaModifiedField, fcaFieldKey, fcaFieldNestedKey],
  );
  const fcaHighlightPath = useMemo(
    () => (fcaFieldNestedKey ? [fcaFieldKey, fcaFieldNestedKey] : [fcaFieldKey]),
    [fcaFieldKey, fcaFieldNestedKey],
  );

  const applyLookupAddress = useCallback(
    (address: AddressDetailsResponse) => {
      if (!form || !lookupBasePath) return;
      form.setFieldValue(
        [...lookupBasePath, 'lineItems'],
        [address.line1, address.line2, address.line3, address.line4, address.county],
      );
      form.setFieldValue([...lookupBasePath, 'city'], address.town);
      form.setFieldValue([...lookupBasePath, 'postCode'], address.postCode);
      form.setFieldValue([...lookupBasePath, 'country'], address.country);
      // setFieldValue does not fire the form's onValuesChange, so the caller has to re-enable Save.
      onAddressApplied?.();
      clearFca();
    },
    [form, lookupBasePath, onAddressApplied, clearFca],
  );

  const addressTitle = useMemo(
    () => (
      <div className={styles.sectionTitleRow}>
        <span className={styles.sectionTitleLabel}>
          {t(titleKey)}
          {isFcaRegistered && isFromFca && (
            <Tag variant="outlined" color="blue" className={styles.fcaTag}>
              {t('firms.labels.fca')}
            </Tag>
          )}
        </span>
        <div>
          {!readonly && (
            <Button
              type="link"
              aria-label={`${isEditing ? t('enso.buttons.close') : t('enso.buttons.edit')} ${t(titleKey)}`}
              icon={isEditing ? <CloseOutlined /> : <EditOutlined />}
              onClick={() => setIsEditing(!isEditing)}
            />
          )}
        </div>
      </div>
    ),
    [readonly, t, isEditing, isFcaRegistered, isFromFca, titleKey],
  );

  const detailColumns: EnsoFormItem[] = useMemo(
    () => [
      {
        valueType: 'group' as const,
        title: addressTitle,
        columns: [],
        colProps: { span: 24 },
      },
      ...(enableLookup && !readonly
        ? [
            {
              valueType: 'group' as const,
              rowProps: { style: { marginBottom: 16 } },
              columns: [
                {
                  title: t('firms.labels.findAddress'),
                  renderFormItem: () => <AddressLookup onAddressFound={applyLookupAddress} />,
                  formItemProps: { colon: false },
                  colProps: { span: 24 },
                },
              ],
            },
          ]
        : []),
      {
        valueType: 'group' as const,
        rowProps: { style: { marginBottom: 16 } },
        columns: [
          {
            title: t('firms.labels.addressLine1'),
            dataIndex: prefixPath(['lineItems', 0]),
            valueType: 'text' as const,
            readonly,
            fieldProps: () => ({
              ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
              placeholder: t('firms.labels.addressLine1Placeholder'),
              className: getHighlightProps(fcaHighlightPath, activeFcaFields),
              onBlur: clearFca,
            }),
            formItemProps: { required: markRequired },
            colProps: { span: 12 },
          },
          {
            title: t('firms.labels.addressLine2'),
            dataIndex: prefixPath(['lineItems', 1]),
            valueType: 'text' as const,
            readonly,
            fieldProps: {
              ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
              placeholder: t('firms.labels.addressLine2Placeholder'),
              className: getHighlightProps(fcaHighlightPath, activeFcaFields),
              onBlur: clearFca,
            },
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group' as const,
        rowProps: { style: { marginBottom: 16 } },
        columns: [
          {
            title: t('firms.labels.addressLine3'),
            dataIndex: prefixPath(['lineItems', 2]),
            valueType: 'text' as const,
            readonly,
            fieldProps: {
              ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
              className: getHighlightProps(fcaHighlightPath, activeFcaFields),
              onBlur: clearFca,
            },
            colProps: { span: 12 },
          },
          {
            title: t('firms.labels.addressLine4'),
            dataIndex: prefixPath(['lineItems', 3]),
            valueType: 'text' as const,
            readonly,
            fieldProps: {
              ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
              className: getHighlightProps(fcaHighlightPath, activeFcaFields),
              onBlur: clearFca,
            },
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group' as const,
        rowProps: { style: { marginBottom: 16 } },
        columns: [
          {
            title: t('firms.labels.town'),
            dataIndex: prefixPath(['city']),
            valueType: 'text' as const,
            readonly,
            fieldProps: {
              ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
              className: getHighlightProps(fcaHighlightPath, activeFcaFields),
              onBlur: clearFca,
            },
            formItemProps: { required: markRequired },
            colProps: { span: 12 },
          },
          {
            title: t('firms.labels.county'),
            dataIndex: prefixPath(['lineItems', 4]),
            valueType: 'text' as const,
            readonly,
            fieldProps: {
              ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
              className: getHighlightProps(fcaHighlightPath, activeFcaFields),
              onBlur: clearFca,
            },
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group' as const,
        rowProps: { style: { marginBottom: 16 } },
        columns: [
          {
            title: t('firms.labels.postCode'),
            dataIndex: prefixPath(['postCode']),
            valueType: 'text' as const,
            readonly,
            fieldProps: {
              ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
              className: getHighlightProps(fcaHighlightPath, activeFcaFields),
              onBlur: clearFca,
            },
            formItemProps: { required: markRequired },
            colProps: { span: 12 },
          },
          {
            title: t('firms.labels.country'),
            dataIndex: prefixPath(['country']),
            valueType: 'text' as const,
            readonly,
            fieldProps: {
              ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
              className: getHighlightProps(fcaHighlightPath, activeFcaFields),
              onBlur: clearFca,
            },
            colProps: { span: 12 },
          },
        ],
      },
    ],
    [t, readonly, addressTitle, activeFcaFields, prefixPath, fcaHighlightPath, clearFca, markRequired, enableLookup, applyLookupAddress],
  );

  const previewColumns: EnsoFormItem[] = useMemo(
    () => [
      {
        valueType: 'group' as const,
        columns: [
          {
            title: addressTitle,
            renderFormItem: () => (
              <Input.TextArea
                value={formatAddressAsLines(getAddress?.() ?? {})}
                autoSize={{ minRows: 2 }}
                disabled
                className={getHighlightProps(fcaHighlightPath, activeFcaFields)}
              />
            ),
            formItemProps: { colon: false },
            colProps: { span: 12 },
          },
        ],
      },
    ],
    [getAddress, addressTitle, activeFcaFields, fcaHighlightPath],
  );

  return (
    <CrudSchemaFields
      schema={isEditing ? detailColumns : previewColumns}
      form={form}
      readonly={readonly}
      component={false}
    />
  );
};
